/**
 * School Info Reminder Component
 *
 * Displays a banner/dialog to remind students to update their school information
 * for CAT career orientation survey statistics
 */

import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@vsat/shared/store/authStore';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@vsat/shared/components';
import { Button } from '@vsat/shared/components';
import { School, AlertCircle } from 'lucide-react';
import { accountService } from '../../services/accountService';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY_PREFIX = 'school_info_reminder_dismissed_';
const SHOW_DELAY_MS = 3000; // Show after 3 seconds to avoid annoying users immediately

export function SchoolInfoReminder() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuthStore();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSchoolInfo, setHasSchoolInfo] = useState<boolean | null>(null);

  // Don't show on account page (redundant) or exam pages
  const isAccountPage = location.pathname === '/account';
  const isExamPage = location.pathname.includes('/lam-bai');
  const isLoginPage = location.pathname === '/login' || location.pathname === '/register';

  useEffect(() => {
    // Only check if authenticated and not on excluded pages
    if (!isAuthenticated || !user?.id || isAccountPage || isExamPage || isLoginPage) {
      setOpen(false);
      return;
    }

    let timer: NodeJS.Timeout | null = null;

    // Check profile for school information
    const checkSchoolInfo = async () => {
      setLoading(true);
      try {
        const profile = await accountService.getProfile();
        const hasSchool = !!(profile.school && profile.school.trim());

        setHasSchoolInfo(hasSchool);

        // Only proceed if school info is missing
        if (!hasSchool) {
          // Check if already dismissed for this user
          const storageKey = `${STORAGE_KEY_PREFIX}${user.id}`;
          const dismissedTimestamp = localStorage.getItem(storageKey);

          // If dismissed recently (within last 24 hours), don't show again
          if (dismissedTimestamp) {
            const dismissedTime = parseInt(dismissedTimestamp, 10);
            const now = Date.now();
            const hoursSinceDismissed = (now - dismissedTime) / (1000 * 60 * 60);

            // Show again after 24 hours
            if (hoursSinceDismissed < 24) {
              setLoading(false);
              return;
            }
          }

          // Add delay before showing to avoid annoying users immediately
          timer = setTimeout(() => {
            // Double-check dismissal status after delay (user might have dismissed in another tab)
            const currentDismissed = localStorage.getItem(storageKey);
            const shouldShow =
              !currentDismissed ||
              parseInt(currentDismissed, 10) + 24 * 60 * 60 * 1000 < Date.now();

            if (shouldShow) {
              setOpen(true);
            }
          }, SHOW_DELAY_MS);
        } else {
          // User has school info, clear any dismissal state
          const storageKey = `${STORAGE_KEY_PREFIX}${user.id}`;
          localStorage.removeItem(storageKey);
        }
      } catch (error) {
        console.error('Failed to check school info:', error);
        // Silently fail - don't show error to user for this background check
      } finally {
        setLoading(false);
      }
    };

    checkSchoolInfo();

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isAuthenticated, user?.id, location.pathname, isAccountPage, isExamPage, isLoginPage]);

  // Re-check when navigating away from account page (user might have updated school info)
  useEffect(() => {
    // Only re-check if we just left the account page and previously didn't have school info
    if (
      location.pathname !== '/account' &&
      isAuthenticated &&
      user?.id &&
      hasSchoolInfo === false &&
      !isExamPage &&
      !isLoginPage
    ) {
      // Add a small delay to allow profile update to propagate
      const checkTimer = setTimeout(async () => {
        try {
          const profile = await accountService.getProfile();
          const hasSchool = !!(profile.school && profile.school.trim());

          if (hasSchool) {
            setHasSchoolInfo(true);
            setOpen(false);
            // Clear dismissal state since user has updated
            const storageKey = `${STORAGE_KEY_PREFIX}${user.id}`;
            localStorage.removeItem(storageKey);
          }
        } catch (error) {
          // Silently fail - avoid spamming API on every navigation
        }
      }, 1000); // 1 second delay to avoid immediate re-check

      return () => clearTimeout(checkTimer);
    }
  }, [location.pathname, isAuthenticated, user?.id, hasSchoolInfo, isExamPage, isLoginPage]);

  const handleUpdateNow = () => {
    setOpen(false);
    // Navigate to account page with personal tab focus
    navigate('/account', { state: { activeTab: 'personal' } });
  };

  const handleDismiss = () => {
    setOpen(false);
    if (user?.id) {
      // Store dismissal timestamp for this user
      const storageKey = `${STORAGE_KEY_PREFIX}${user.id}`;
      localStorage.setItem(storageKey, Date.now().toString());
    }
  };

  // Don't render if not authenticated, has school info, or on excluded pages
  if (!isAuthenticated || hasSchoolInfo !== false || isAccountPage || isExamPage || isLoginPage) {
    return null;
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      // If user closes dialog (ESC, click outside), treat as dismiss
      handleDismiss();
    }
  };

  return (
    <Dialog open={open && !loading} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-md border-orange-200 dark:border-orange-900 bg-white dark:bg-gray-900'>
        <DialogHeader>
          <div className='flex items-center gap-3 mb-2'>
            <div className='p-2 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'>
              <School className='w-6 h-6' />
            </div>
            <DialogTitle className='text-xl text-gray-900 dark:text-white'>
              {t('account.schoolReminder.title')}
            </DialogTitle>
          </div>
          <DialogDescription className='text-gray-600 dark:text-gray-300 text-base'>
            {t('account.schoolReminder.message')}
          </DialogDescription>
        </DialogHeader>

        <div className='py-4'>
          <div className='flex items-start gap-3 p-4 rounded-lg bg-orange-50 dark:bg-orange-900/10 border border-orange-100 dark:border-orange-900/30'>
            <AlertCircle className='w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0' />
            <p className='text-sm text-gray-700 dark:text-gray-200'>
              {t('account.schoolReminder.note')}
            </p>
          </div>
        </div>

        <DialogFooter className='flex flex-col-reverse sm:flex-row gap-2'>
          <Button variant='outline' onClick={handleDismiss} className='w-full sm:w-auto'>
            {t('account.schoolReminder.dismiss')}
          </Button>
          <Button
            onClick={handleUpdateNow}
            className='w-full sm:w-auto bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white border-0'
          >
            <School className='w-4 h-4 mr-2' />
            {t('account.schoolReminder.updateNow')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
